<template>
  <ListItem :to="to" variant="warning">
    <div class="flex w-full items-start gap-2 text-sm max-sm:flex-col md:items-center md:gap-4">
      <div
        class="flex basis-[15%] flex-col justify-center self-stretch rounded-sm bg-white/80 p-1 text-center"
        style="color: var(--content-color)"
      >
        <p class="flex items-center justify-center font-semibold">
          <template v-if="isDelivery">
            <ArrowRightFromLineIcon class="inline-icon" />{{ t('delivery') }}
          </template>
          <template v-else> <ArrowLeftToLineIcon class="inline-icon" />{{ t('return') }} </template>
        </p>
      </div>
      <div class="basis-[40%]">
        <p class="font-semibold">
          {{ booking?.customerData?.firstName ?? 'n/a' }}
          {{ booking?.customerData?.lastName ?? 'n/a' }}
        </p>
        <p>
          <LicencePlate>{{ transfer.car?.licencePlate }}</LicencePlate>
          {{ transfer.car?.vehicleType?.make.name }}
          {{ transfer.car?.vehicleType?.model }}
        </p>
      </div>
      <div class="grid w-full basis-[45%] max-sm:grid-cols-2">
        <div class="mb-1 flex items-center gap-4">
          <span><ArrowRightFromLineIcon class="inline-icon" />{{ startDate }}</span>
          <span><ClockIcon class="inline-icon" />{{ formattedTravelTime }}</span>
        </div>
        <div class="flex items-center gap-1">
          <NavigationIcon class="inline-icon" />
          <span>{{ getLocationText(transfer, 'pickup', locale) }}</span>
          <ArrowRightIcon class="inline-icon" />
          <span>{{ getLocationText(transfer, 'dropoff', locale) }}</span>
        </div>
      </div>
    </div>
    <template v-if="booking" #suffix>
      <ListItemAnnotation :to="{ name: 'bookingEdit', params: { id: booking.id } }">
        <div
          class="flex flex-1 items-center gap-x-3 text-sm font-semibold max-sm:flex-col max-sm:items-start"
        >
          <div class="font-normal">{{ t('fromBooking') }}:</div>
          <div><span class="text-obsidian-400"># </span>{{ booking.bookingNumber }}</div>
          <div class="flex items-center gap-3">
            <span
              ><ArrowRightFromLineIcon class="inline-icon text-hazel-500" />{{
                bookingStartDate
              }}</span
            >
            <span
              ><ArrowLeftToLineIcon class="inline-icon text-hazel-500" />{{ bookingEndDate }}</span
            >
          </div>
        </div>
      </ListItemAnnotation>
    </template>
  </ListItem>
</template>

<script setup lang="ts">
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import LicencePlate from '@/components/LicencePlate.vue';
import {
  ArrowLeftToLineIcon,
  ArrowRightFromLineIcon,
  ArrowRightIcon,
  ClockIcon,
  NavigationIcon,
} from 'lucide-vue-next';
import ListItem from '@/components/ListItem.vue';
import ListItemAnnotation from '@/components/ListItemAnnotation.vue';
import { AllocationType } from '@carvia/ros-client-types';
import { DateTime } from 'luxon';
import { formatDurationMinutes } from '@/utils/duration-input';
import { getLocationText } from '@/utils/get-location-text';

const props = defineProps<{
  transfer: Allocation;
}>();
const { t, locale } = useI18n();
const dateFormat = 'dd.MM.yyyy - HH:mm';

const booking = computed(() => props.transfer.transferOf?.booking ?? props.transfer.booking);
const startDate = useFormattedDate(() => props.transfer.startDate, dateFormat);
const bookingStartDate = useFormattedDate(() => booking.value?.startDate, dateFormat);
const bookingEndDate = useFormattedDate(() => booking.value?.endDate, dateFormat);

const isDelivery = computed(() => props.transfer.type === AllocationType.TRANSFER_DELIVERY);

const toDateTime = (value: Date | string) =>
  value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value);

const formattedTravelTime = computed(() => {
  const travelMinutes =
    props.transfer.customTransferMinutes ??
    Math.round(
      toDateTime(props.transfer.endDate).diff(toDateTime(props.transfer.startDate), 'minutes')
        .minutes,
    ) - (props.transfer.bufferMinutes ?? 0);
  return formatDurationMinutes(Math.max(0, travelMinutes));
});

const to = computed(() => {
  if (props.transfer.transferOf?.id) {
    return {
      name: 'transferEdit',
      params: {
        transferType: props.transfer.type,
        allocationId: props.transfer.transferOf.id,
        transferId: props.transfer.id,
      },
    };
  }
  return { name: 'transferDetail', params: { id: props.transfer.id } };
});
</script>

<i18n lang="json">
{
  "en": {
    "delivery": "Delivery",
    "return": "Return",
    "fromBooking": "From booking"
  },
  "de": {
    "delivery": "Anlieferung",
    "return": "Rückführung",
    "fromBooking": "Von Buchung"
  },
  "es": {
    "delivery": "Entrega",
    "return": "Recogida",
    "fromBooking": "De la reserva"
  }
}
</i18n>
